import { CommonModule } from '@angular/common'
import { Component, ElementRef, Input, Renderer2, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Business } from '../../../../store/business/business.state'
import { NumberToWordsPipe } from '../../../../pipes/number-to-words.pipe'

export interface InvoiceItem {
  description: string
  qty: number
  cost: number
  discount: number
  total: number
}

export interface InvoiceData {
  invoiceDisplayId: string
  createdAt: string
  dueDate: string
  fromName: string
  fromAddress: string
  fromEmail: string
  fromPhone: string
  toName: string
  toAddress: string
  toEmail: string
  toPhone: string
  paymentStatus: string
  items: InvoiceItem[]
  subTotal: number
  discountPercent: number
  discountAmount: number
  vatPercent: number
  vatAmount: number
  totalAmount: number
  amountInWords: string

  totalLicences: number
  chargeAmount: number
  amount: number
  nextBillingDate: string
  payments: Payment[]
  planTotalCost: number
  licencesChangeCount: number
}

export interface Payment {
  id: string
  name: string
  businessId: string
  subscriptionId: string
  amount: number
  currency: string
  status: 'success' | 'failed' | 'pending'
  gateway: string
  gatewayPaymentId: string
  gatewayOrderId: string | null
  gatewayResponse: GatewayResponse
  gstRate: string
  gstAmount: string
  cgst: string
  sgst: string
  igst: string
  taxableAmount: string
  licenceId: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  paymentResponse: string | null
  paymentMethod?: string

  cardLast4?: string
  cardNetwork?: string
  cardType?: string
  cardIssuer?: string
  upiVpa?: string
  bankName?: string
}

export interface GatewayResponse {
  id: string
  notes: GatewayNotes
  amount: number
  entity: string
  status: string
  receipt: string
  attempts: number
  checkout: string | null
  currency: string
  offer_id: string | null
  amount_due: number
  created_at: number
  amount_paid: number
  description: string | null
}

export interface GatewayNotes {
  amount: number
  context: string
  couponCode: string
  gstBreakup: GstBreakup
  usedLicences: number
  gatewayPlanId: string
  licencesCount: number
  couponDiscount: number
  gatewayCustomerId: string
  nextBillingStartAt: string
  internalSubscriptionId: string
  subTotal: number
  taxableAmount: number
  proratedCost: number
}

export interface GstBreakup {
  gstRate: number
  gstAmount: number
  cgst: number
  sgst: number
  igst: number
  taxableAmount: number
}

@Component({
  standalone: true,
  selector: 'app-licence-history',
  imports: [CommonModule, ReactiveFormsModule, CapitalizePipe, NumberToWordsPipe],
  templateUrl: './subscription-invoice.component.html',
  styleUrls: ['./subscription-invoice.component.scss']
})
export class SubscriptionInvoiceComponent {
  @Input() invoiceData!: InvoiceData
  //   licence = this.invoiceData.licence
  //   business = this.invoiceData.business
  //   adminBusiness = this.invoiceData.adminBusiness
  @Input() businessData!: Business
  @Input() adminBusinessData!: Business
  @Input() subscriptionData!: any
  isDownloading = false
  @ViewChild('invoicePrintArea', { static: true }) invoicePrintArea!: ElementRef
  isPrinting = false
  constructor(
    public activeModal: NgbActiveModal,
    private renderer: Renderer2
  ) {}

  closeModal(): void {
    this.activeModal.dismiss()
  }

  //   printInvoice(): void {
  //     const printContents = this.invoicePrintArea.nativeElement.innerHTML
  //     const printWindow = window.open('', '_blank', 'width=900,height=700')
  //     if (!printWindow) return

  //     const stylesHtml = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
  //       .map(el => el.outerHTML)
  //       .join('')

  //     printWindow.document.write(`
  //         <html>
  //           <head>
  //             <base href="${document.baseURI}">
  //             <title>Invoice ${this.invoiceData.invoiceNo}</title>
  //             ${stylesHtml}
  //             <style> body { padding: 20px; } </style>
  //           </head>
  //           <body>${printContents}</body>
  //         </html>
  //       `)
  //     printWindow.document.close()
  //     printWindow.focus()
  //     setTimeout(() => {
  //       printWindow.print()
  //       printWindow.close()
  //     }, 400)
  //   }

  public async printInvoice(): Promise<void> {
    if (!this.invoiceData) {
      alert('Cannot print: Invoice data is missing.')
      return
    }

    // CRITICAL: window.open sabse pehle, sync context me (click ke andar hi)
    // Warna async gap ke baad popup blocker rok dega
    const previewWindow = window.open('', '_blank')
    if (!previewWindow) {
      alert('Popup blocked hai. Browser me is site ke liye popup allow karo aur dobara try karo.')
      return
    }
    previewWindow.document.write(
      '<p style="font-family:sans-serif;padding:20px;">PDF generating, Please wait...</p>'
    )

    this.isPrinting = true

    // 1. Gather all styles + content HTML from the main document (same as download)
    const stylesHtml = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML)
      .join('')

    const contentHtml = this.invoicePrintArea.nativeElement.outerHTML
    const baseHref = document.baseURI

    // 2. Create iframe and set srcdoc (same as download)
    const iframe = this.renderer.createElement('iframe')
    this.renderer.setStyle(iframe, 'position', 'absolute')
    this.renderer.setStyle(iframe, 'left', '-9999px')
    this.renderer.setStyle(iframe, 'top', '-9999px')
    this.renderer.setAttribute(iframe, 'width', '1200')
    this.renderer.setAttribute(iframe, 'height', '1200')

    iframe.srcdoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <base href="${baseHref}">
        ${stylesHtml}
        <style>
          body { background: #ffffff; padding: 0; margin: 0; }
          i[class*="ti-"] { display: none !important; }
        </style>
      </head>
      <body>
        ${contentHtml}
      </body>
    </html>
  `

    // 3. Wait for iframe to fully load (same as download)
    const promise = new Promise<HTMLIFrameElement>(resolve => {
      iframe.onload = () => resolve(iframe)
    })

    this.renderer.appendChild(document.body, iframe)

    try {
      await promise

      const iframeDoc = iframe.contentWindow.document
      const elementToRender = iframeDoc.body.firstElementChild as HTMLElement

      // 4. Run html2canvas on iframe's content (same as download)
      const canvas = await html2canvas(elementToRender, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      })

      // 5. Generate PDF (multi-page) — same as download
      const imgWidthMM = 190
      const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width
      const contentDataURL = canvas.toDataURL('image/png', 1.0)
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfPageWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      const pdfTopMargin = 10

      if (imgHeightMM <= pdfPageHeight - pdfTopMargin - 10) {
        pdf.addImage(
          contentDataURL,
          'PNG',
          (pdfPageWidth - imgWidthMM) / 2,
          pdfTopMargin,
          imgWidthMM,
          imgHeightMM
        )
      } else {
        let currentYCanvasPx = 0
        let remainingImageHeightMM = imgHeightMM
        let firstPage = true

        while (remainingImageHeightMM > 0) {
          if (!firstPage) pdf.addPage()

          const pageChunkHeightMM = firstPage
            ? Math.min(remainingImageHeightMM, pdfPageHeight - pdfTopMargin - 10)
            : Math.min(remainingImageHeightMM, pdfPageHeight - 10 - 10)

          const segmentSourceHeightPx = (pageChunkHeightMM / imgHeightMM) * canvas.height
          const tempCanvas = document.createElement('canvas')
          tempCanvas.width = canvas.width
          tempCanvas.height = segmentSourceHeightPx
          const tempCtx = tempCanvas.getContext('2d')

          if (tempCtx) {
            tempCtx.drawImage(
              canvas,
              0,
              currentYCanvasPx,
              canvas.width,
              segmentSourceHeightPx,
              0,
              0,
              canvas.width,
              segmentSourceHeightPx
            )
            const segmentDataURL = tempCanvas.toDataURL('image/png', 1.0)
            pdf.addImage(
              segmentDataURL,
              'PNG',
              (pdfPageWidth - imgWidthMM) / 2,
              firstPage ? pdfTopMargin : 10,
              imgWidthMM,
              pageChunkHeightMM
            )
          }

          currentYCanvasPx += segmentSourceHeightPx
          remainingImageHeightMM -= pageChunkHeightMM
          firstPage = false
        }
      }

      // ---- YAHAN SE DIFFERENT: pdf.save() ki jagah blob URL se preview window navigate karo ----
      const blobUrl = pdf.output('bloburl') as unknown as string
      previewWindow.location.href = blobUrl

      // Print dialog trigger karo jab PDF load ho jaye
      previewWindow.onload = () => {
        setTimeout(() => previewWindow.print(), 500)
      }
    } catch (error) {
      console.error('Print PDF generation failed:', error)
      previewWindow.close()
      alert('Failed to generate PDF for printing.')
    } finally {
      this.renderer.removeChild(document.body, iframe)
      this.isPrinting = false
    }
  }

  // ---- DOWNLOAD AS PDF using hidden iframe (real browser CSS rendering) ----
  public async downloadInvoice(): Promise<void> {
    if (!this.invoiceData) {
      alert('Cannot generate PDF: Invoice data is missing.')
      return
    }

    this.isDownloading = true

    // 1. Gather all styles + content HTML from the main document
    const stylesHtml = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML)
      .join('')

    const contentHtml = this.invoicePrintArea.nativeElement.outerHTML
    const baseHref = document.baseURI

    // 2. Create iframe and set srcdoc
    const iframe = this.renderer.createElement('iframe')
    this.renderer.setStyle(iframe, 'position', 'absolute')
    this.renderer.setStyle(iframe, 'left', '-9999px')
    this.renderer.setStyle(iframe, 'top', '-9999px')
    this.renderer.setAttribute(iframe, 'width', '1200')
    this.renderer.setAttribute(iframe, 'height', '1200')

    iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <base href="${baseHref}">
          ${stylesHtml}
          <style>
            body { background: #ffffff; padding: 0; margin: 0; }
            /* Icon fonts canvas me tootey hue box dikhate hain, hide kar do */
            i[class*="ti-"] { display: none !important; }
          </style>
        </head>
        <body>
          ${contentHtml}
        </body>
      </html>
    `

    // 3. Wait for iframe to fully load (CSS, fonts, images)
    const promise = new Promise<HTMLIFrameElement>(resolve => {
      iframe.onload = () => resolve(iframe)
    })

    this.renderer.appendChild(document.body, iframe)

    try {
      await promise

      const iframeDoc = iframe.contentWindow.document
      const elementToRender = iframeDoc.body.firstElementChild as HTMLElement

      // 4. Run html2canvas on iframe's content
      const canvas = await html2canvas(elementToRender, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      })

      // 5. Generate PDF (multi-page)
      const imgWidthMM = 190
      const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width
      const contentDataURL = canvas.toDataURL('image/png', 1.0)
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfPageWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      const pdfTopMargin = 10

      if (imgHeightMM <= pdfPageHeight - pdfTopMargin - 10) {
        pdf.addImage(
          contentDataURL,
          'PNG',
          (pdfPageWidth - imgWidthMM) / 2,
          pdfTopMargin,
          imgWidthMM,
          imgHeightMM
        )
      } else {
        let currentYCanvasPx = 0
        let remainingImageHeightMM = imgHeightMM
        let firstPage = true

        while (remainingImageHeightMM > 0) {
          if (!firstPage) pdf.addPage()

          const pageChunkHeightMM = firstPage
            ? Math.min(remainingImageHeightMM, pdfPageHeight - pdfTopMargin - 10)
            : Math.min(remainingImageHeightMM, pdfPageHeight - 10 - 10)

          const segmentSourceHeightPx = (pageChunkHeightMM / imgHeightMM) * canvas.height
          const tempCanvas = document.createElement('canvas')
          tempCanvas.width = canvas.width
          tempCanvas.height = segmentSourceHeightPx
          const tempCtx = tempCanvas.getContext('2d')

          if (tempCtx) {
            tempCtx.drawImage(
              canvas,
              0,
              currentYCanvasPx,
              canvas.width,
              segmentSourceHeightPx,
              0,
              0,
              canvas.width,
              segmentSourceHeightPx
            )
            const segmentDataURL = tempCanvas.toDataURL('image/png', 1.0)
            pdf.addImage(
              segmentDataURL,
              'PNG',
              (pdfPageWidth - imgWidthMM) / 2,
              firstPage ? pdfTopMargin : 10,
              imgWidthMM,
              pageChunkHeightMM
            )
          }

          currentYCanvasPx += segmentSourceHeightPx
          remainingImageHeightMM -= pageChunkHeightMM
          firstPage = false
        }
      }

      pdf.save(`Invoice-${this.invoiceData.invoiceDisplayId}.pdf`)
    } catch (error) {
      console.error('PDF generation failed:', error)
      alert('Failed to generate PDF.')
    } finally {
      this.renderer.removeChild(document.body, iframe)
      this.isDownloading = false
    }
  }

  get payment() {
    return this.invoiceData?.payments?.[0]
  }

  get halfGstRate(): number {
    return Number(this.payment?.gstRate ?? 0) / 2
  }

  getLicenceAmount(): number | null {
    // const taxableAmount = this.invoiceData?.payments?.[0]?.taxableAmount
    // const taxableAmount = this.invoiceData?.amount
    const taxableAmount = this.invoiceData?.chargeAmount
    const count = this.invoiceData?.licencesChangeCount

    if (taxableAmount === null || count === null || Number(count) === 0) {
      return null
    }

    return Number((Number(taxableAmount) / Number(count)).toFixed(2))
  }
}
